/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode } from 'react';

import { CalendarIcon, CalendarRangeIcon } from '../Icons';
import { ResponsiveOverlay } from '../Overlays';
import { Button } from '../Buttons';
import { Calendar, CalendarProps, DayPickerMode } from './Calendar';

import { cn } from 'styles';
import { formatDate } from 'utils/date';

type DatePickProps<Mode extends DayPickerMode> = Omit<CalendarProps<Mode>, 'mode'> & {
  placeholder?: string;
  children?: ReactNode;

  mode?: Mode;

  format?: (date: NonNullable<CalendarProps<Mode>['selected']>) => ReactNode;

  /**
   * Choose how the picker is opened on smaller screens
   *
   * Defaults to `drawer`
   */
  responsiveAs?: 'pop-over' | 'modal' | 'drawer';
};

const modeToFormat: {
  [M in DayPickerMode]: (date: NonNullable<CalendarProps<M>['selected']>) => ReactNode;
} = {
  single: (date) => formatDate(date, 'dd/mm/yyyy'),

  range: ({ from, to }) =>
    [
      from ? formatDate(from, 'dd/mm/yyyy') : 'XX',
      ' - ',
      to ? formatDate(to, 'dd/mm/yyyy') : 'YY',
    ].join(''),

  multiple: (dates) => dates.map((d) => formatDate(d, 'dd/mm/yyyy')),
};

/*
 Extra confirm logic to handle:

 - Open state (close it after confirmation should be a prop)
 - inner state x outer, any outer change should sync the inner
 - open as popover, modal, drawer, sides-sheet? => possibility to change on mobile
 - save onSelect params to pass to handler when confirmation
 - define how to handle situations when confirm => triggers onSelect but than the upper state is NOT updated due to logic on that handler

*/

export function DatePicker<Mode extends DayPickerMode = 'single'>({
  className,
  placeholder = 'Pick a date',
  children,
  format,
  responsiveAs,
  ...props
}: DatePickProps<Mode>) {
  return (
    <ResponsiveOverlay
      default="pop-over"
      switchTo={responsiveAs}
      showCloseButton={false}
      className="w-auto p-0 phone:p-0"
      align="start"
      trigger={
        children ?? (
          <Button
            variant="outlined"
            size="medium"
            color="border"
            disabled={props.disabled === true}
            className={cn(
              'justify-start text-left font-normal text-muted-foreground hover:bg-hover',
              className,
            )}
          >
            {props.mode === 'range' ? <CalendarRangeIcon size={16} /> : <CalendarIcon size={16} />}

            <span>
              {props.selected
                ? format?.(props.selected as any) ??
                  modeToFormat[props.mode ?? 'single'](props.selected as any)
                : placeholder}
            </span>
          </Button>
        )
      }
    >
      <Calendar mode="single" {...(props as any)} initialFocus />

      {/*
          To do: make it able to change around without triggering onSelect until this is clicked
          To do: make closing the range optionally trigger onSelect by props as well
          control it via multiple props: a prop that enables this, one that verifies if the selection is valid (so we update internally or not [eg: range selections when the user makes the null, single selections when the user de-selects...])
          <Button size="small" className="mx-auto w-full max-w-[90%]">
            Confirmar
          </Button>
      */}
    </ResponsiveOverlay>
  );
}
